import { Plus, Trash2, Zap } from "lucide-react";
import { useState } from "react";
import {
  useCreate,
  useDelete,
  useGetList,
  useNotify,
  useRefresh,
} from "ra-core";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import { useConfigurationContext } from "../../atomic-crm/root/ConfigurationContext";
import { LIFECYCLE_STAGE_CHOICES } from "../../atomic-crm/contacts/crmEnrichmentModel";
import { EmptyState } from "../data-display/EmptyState";
import { SectionCard } from "../data-display/SectionCard";
import {
  ACTIONS,
  DELAY_CHOICES,
  type Workflow,
  type WorkflowAction,
  actionLabel,
  describeDelay,
} from "./workflowModel";
import { MessagePreview } from "../comms/MessagePreview";
import { TemplatePicker, TokenChips } from "../comms/MessageTokens";
import { useTokenInsert } from "../comms/useTokenInsert";

const inputClass =
  "h-9 w-full rounded-md border border-input bg-transparent px-2.5 text-[13px] outline-none focus-visible:ring-2 focus-visible:ring-ring";

interface Choice {
  id: string | number;
  name: string;
}

/** One-line human summary of a configured action, for the step list. */
const describeAction = (
  action: WorkflowAction,
  lookups: { sequences: Choice[]; tags: Choice[] },
): string => {
  const cfg = action.config ?? {};
  switch (action.action_type) {
    case "create_task":
      return cfg.text ? `"${cfg.text}"` : "Automated follow-up";
    case "send_message":
      return `${(cfg.channel ?? "email").toUpperCase()}, ${
        cfg.subject || cfg.body?.slice(0, 60) || "no message yet"
      }`;
    case "enroll_sequence":
      return (
        lookups.sequences.find((s) => String(s.id) === cfg.sequence_id)?.name ??
        "no sequence chosen"
      );
    case "set_lifecycle_stage":
      return cfg.lifecycle_stage ?? "no stage chosen";
    case "add_tag":
      return (
        lookups.tags.find((t) => String(t.id) === cfg.tag_id)?.name ??
        "no tag chosen"
      );
    case "set_deal_stage":
      return cfg.stage ?? "no stage chosen";
    case "create_job":
      return cfg.title || "Automated job";
    case "ai_draft_followup":
      return `${cfg.channel ?? "email"} draft, filed as a task to review`;
    default:
      return "";
  }
};

/**
 * Ordered action list + an add-action form. Actions run in order, each after
 * its own delay; the engine records every attempt (including consent
 * suppressions) on the run ledger.
 */
export const WorkflowActionsEditor = ({ workflow }: { workflow: Workflow }) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const [create] = useCreate();
  const [deleteOne] = useDelete();
  const { dealStages } = useConfigurationContext();

  const { data: actions } = useGetList<WorkflowAction>("workflow_actions", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "position", order: "ASC" },
    filter: { workflow_id: workflow.id },
  });
  const { data: sequences } = useGetList<Choice>("sequences", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "name", order: "ASC" },
  });
  const { data: tags } = useGetList<Choice>("tags", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "name", order: "ASC" },
  });

  const [actionType, setActionType] = useState<string>("create_task");
  const [delay, setDelay] = useState(0);
  const [config, setConfig] = useState<Record<string, string>>({});
  const [onlyIfNoReply, setOnlyIfNoReply] = useState(false);

  const setField = (key: string, value: string) =>
    setConfig((prev) => ({ ...prev, [key]: value }));

  // Token chips insert at the CARET, so someone who clicked mid-sentence gets
  // the token where they are looking rather than appended at the end.
  const bodyInsert = useTokenInsert(config.body ?? "", (next) =>
    setField("body", next),
  );

  const addAction = () => {
    create(
      "workflow_actions",
      {
        data: {
          workflow_id: workflow.id,
          position: actions?.length ?? 0,
          action_type: actionType,
          delay_minutes: delay,
          config,
          // Evaluated when the action is DUE, not when the run starts, the
          // whole point of a delayed nudge is that the customer may have
          // answered during the delay.
          conditions: onlyIfNoReply ? { if_no_reply: true } : {},
        },
      },
      {
        mutationMode: "pessimistic",
        onSuccess: () => {
          notify("Action added");
          setConfig({});
          setOnlyIfNoReply(false);
          refresh();
        },
        onError: (error: unknown) =>
          notify(
            error instanceof Error ? error.message : "Could not add the action",
            { type: "error" },
          ),
      },
    );
  };

  return (
    <SectionCard
      icon={Zap}
      title="Actions"
      subtitle="Run in order, each after its own delay"
    >
      {(actions ?? []).length === 0 ? (
        <div className="py-6">
          <EmptyState
            icon={Zap}
            title="No actions yet"
            description="Add what should happen when this workflow triggers."
          />
        </div>
      ) : (
        <div className="mb-4 flex flex-col gap-2">
          {(actions ?? []).map((action, index) => (
            <div
              key={action.id}
              className="flex items-start gap-3 rounded-md border border-border-subtle p-3"
            >
              <div className="flex size-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[12px] font-semibold text-primary">
                {index + 1}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[13px] font-medium">
                  {actionLabel(action.action_type)}
                  <span className="ml-2 font-normal text-muted-foreground">
                    {describeDelay(action.delay_minutes, index === 0)}
                  </span>
                  {(action.conditions as { if_no_reply?: boolean } | null)
                    ?.if_no_reply ? (
                    <span className="ml-2 rounded-full bg-amber-500/10 px-2 py-0.5 text-[11px] font-normal text-amber-700">
                      only if no reply
                    </span>
                  ) : null}
                </p>
                <p className="truncate text-[12px] text-muted-foreground">
                  {describeAction(action, {
                    sequences: sequences ?? [],
                    tags: tags ?? [],
                  })}
                </p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="size-7 text-muted-foreground hover:text-destructive"
                onClick={() =>
                  deleteOne(
                    "workflow_actions",
                    { id: action.id, previousData: action },
                    { mutationMode: "pessimistic", onSuccess: () => refresh() },
                  )
                }
              >
                <Trash2 className="size-3.5" />
              </Button>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-col gap-3 border-t border-border-subtle pt-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="flex flex-col gap-1 text-[12.5px] font-medium">
            Then
            <select
              className={inputClass}
              value={actionType}
              onChange={(e) => {
                setActionType(e.target.value);
                setConfig({});
              }}
            >
              {ACTIONS.map((a) => (
                <option key={a.value} value={a.value}>
                  {a.label}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-[12.5px] font-medium">
            When
            <select
              className={inputClass}
              value={delay}
              onChange={(e) => setDelay(Number(e.target.value))}
            >
              {DELAY_CHOICES.map((d) => (
                <option key={d.value} value={d.value}>
                  {d.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        <label className="flex items-start gap-2 text-[12.5px]">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={onlyIfNoReply}
            onChange={(e) => setOnlyIfNoReply(e.target.checked)}
          />
          <span>
            Only if the customer hasn&apos;t replied
            <span className="block text-[11.5px] font-normal text-muted-foreground">
              Checked when this step is due, not when the automation starts, if
              they answered during the wait, this step is skipped instead of
              nagging them.
            </span>
          </span>
        </label>

        {actionType === "create_task" && (
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="flex flex-col gap-1 text-[12.5px] font-medium sm:col-span-2">
              Task text
              <input
                className={inputClass}
                placeholder="Call {{first_name}} about their estimate"
                value={config.text ?? ""}
                onChange={(e) => setField("text", e.target.value)}
              />
            </label>
            <label className="flex flex-col gap-1 text-[12.5px] font-medium">
              Due in (days)
              <input
                type="number"
                min={0}
                className={inputClass}
                value={config.due_in_days ?? "0"}
                onChange={(e) => setField("due_in_days", e.target.value)}
              />
            </label>
          </div>
        )}

        {actionType === "send_message" && (
          <div className="flex flex-col gap-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="flex flex-col gap-1 text-[12.5px] font-medium">
                Channel
                <select
                  className={inputClass}
                  value={config.channel ?? "email"}
                  onChange={(e) => setField("channel", e.target.value)}
                >
                  <option value="email">Email</option>
                  <option value="sms">SMS</option>
                </select>
              </label>
              {(config.channel ?? "email") === "email" && (
                <label className="flex flex-col gap-1 text-[12.5px] font-medium">
                  Subject
                  <input
                    className={inputClass}
                    value={config.subject ?? ""}
                    onChange={(e) => setField("subject", e.target.value)}
                  />
                </label>
              )}
            </div>
            <TemplatePicker
              channel={config.channel ?? "email"}
              onPick={(template: { body: string; subject: string | null }) => {
                setField("body", template.body);
                if (template.subject) setField("subject", template.subject);
              }}
            />

            <label className="flex flex-col gap-1 text-[12.5px] font-medium">
              Message
              <textarea
                ref={bodyInsert.ref}
                className={cn(inputClass, "h-24 resize-y py-2")}
                placeholder="Hi {{first_name}}, …"
                value={config.body ?? ""}
                onChange={(e) => setField("body", e.target.value)}
              />
              <TokenChips onInsert={bodyInsert.insert} className="mt-1" />
              <MessagePreview
                body={config.body ?? ""}
                subject={
                  (config.channel ?? "email") === "email"
                    ? (config.subject ?? null)
                    : null
                }
              />
              <span className="text-[11.5px] font-normal text-muted-foreground">
                Tokens are filled in when the message is sent. Sent through the
                consent gate, opted-out and do-not-contact customers are
                suppressed, never messaged.
              </span>
            </label>
          </div>
        )}

        {actionType === "enroll_sequence" && (
          <label className="flex flex-col gap-1 text-[12.5px] font-medium">
            Sequence
            <select
              className={inputClass}
              value={config.sequence_id ?? ""}
              onChange={(e) => setField("sequence_id", e.target.value)}
            >
              <option value="">Choose a sequence…</option>
              {(sequences ?? []).map((s) => (
                <option key={s.id} value={String(s.id)}>
                  {s.name}
                </option>
              ))}
            </select>
          </label>
        )}

        {actionType === "ai_draft_followup" && (
          <div className="flex flex-col gap-2">
            <label className="flex flex-col gap-1 text-[12.5px] font-medium">
              Channel to draft for
              <select
                className={inputClass}
                value={config.channel ?? "email"}
                onChange={(e) => setField("channel", e.target.value)}
              >
                <option value="email">Email</option>
                <option value="sms">SMS</option>
              </select>
            </label>
            <label className="flex flex-col gap-1 text-[12.5px] font-medium">
              What should the draft aim for? (optional)
              <textarea
                className={cn(inputClass, "h-16 resize-y py-2")}
                placeholder="Nudge them about the open estimate, friendly tone"
                value={config.instruction ?? ""}
                onChange={(e) => setField("instruction", e.target.value)}
              />
            </label>
            <span className="text-[11.5px] text-muted-foreground">
              The AI writes a draft and files it as a task for a person to
              review. It cannot send anything; sending still happens in the
              composer with the usual consent checks.
            </span>
          </div>
        )}

        {actionType === "set_lifecycle_stage" && (
          <label className="flex flex-col gap-1 text-[12.5px] font-medium">
            Lifecycle stage
            <select
              className={inputClass}
              value={config.lifecycle_stage ?? ""}
              onChange={(e) => setField("lifecycle_stage", e.target.value)}
            >
              <option value="">Choose a stage…</option>
              {LIFECYCLE_STAGE_CHOICES.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
        )}

        {actionType === "add_tag" && (
          <label className="flex flex-col gap-1 text-[12.5px] font-medium">
            Tag
            <select
              className={inputClass}
              value={config.tag_id ?? ""}
              onChange={(e) => setField("tag_id", e.target.value)}
            >
              <option value="">Choose a tag…</option>
              {(tags ?? []).map((t) => (
                <option key={t.id} value={String(t.id)}>
                  {t.name}
                </option>
              ))}
            </select>
          </label>
        )}

        {actionType === "set_deal_stage" && (
          <label className="flex flex-col gap-1 text-[12.5px] font-medium">
            Deal stage
            <select
              className={inputClass}
              value={config.stage ?? ""}
              onChange={(e) => setField("stage", e.target.value)}
            >
              <option value="">Choose a stage…</option>
              {dealStages.map((stage) => (
                <option key={stage.value} value={stage.value}>
                  {stage.label}
                </option>
              ))}
            </select>
          </label>
        )}

        {actionType === "create_job" && (
          <label className="flex flex-col gap-1 text-[12.5px] font-medium">
            Job title
            <input
              className={inputClass}
              placeholder="Install for {{first_name}}"
              value={config.title ?? ""}
              onChange={(e) => setField("title", e.target.value)}
            />
          </label>
        )}

        <div className="flex justify-end">
          <Button size="sm" onClick={addAction}>
            <Plus className="mr-1 size-3.5" />
            Add action
          </Button>
        </div>
      </div>
    </SectionCard>
  );
};
